import { useParams, useSearchParams } from 'react-router-dom'

import {
  TrainingFormWithoutQuestionsDocument,
  UpdateTrainingFormDocument,
} from 'generated/resources/graphql'

import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import app from 'modules/core/store/models/app'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { EditModeLayout } from 'modules/trainings/components/EditModeLayout'
import { FormEditMode } from 'modules/trainings/components/Revamp/FormEditMode'
import { FormWizardData } from 'modules/trainings/components/Revamp/NewFormWizard/types'

import { AvailableSections, options } from './editionOptions'

const EditMode = () => {
  const { formId } = useParams()
  const { data, loading } = useQuery(TrainingFormWithoutQuestionsDocument, {
    variables: {
      id: formId ?? '',
    },
  })
  const [updateTrainingForm] = useMutation(UpdateTrainingFormDocument, {
    extractorFn: (data) => data.formsMutations.updateFormsForm,
  })
  const trainingForm = data?.forms.form

  const [searchParams] = useSearchParams()
  const selectedSection = searchParams.get('section') ?? AvailableSections.basic
  const initialData: FormWizardData = {
    anonymous: trainingForm?.anonymous ?? false,
    title: trainingForm?.title ?? '',
    description: trainingForm?.description ?? '',
    trainingFormType: trainingForm?.formType ?? 'satisfaction',
  }

  const form = useForm<FormWizardData>({
    defaultValues: initialData,
    validate: (data) => {
      const errors: FormErrors<FormWizardData> = {}
      if (!data.title) {
        errors.title = 'This field is mandatory'
      }
      return errors
    },
    onSubmit: async (data) => {
      if (!trainingForm || !form) return { success: false, errors: [] }
      const result = await updateTrainingForm({
        variables: {
          ...data,
          id: trainingForm?.id,
        },
      })

      const handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: 'There was an error editing the survey. Please try again',
        })
      })
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: 'The survey has been successfully edited',
        })
      }
      const values = form.values
      form.resetForm({ values })
      return handled
    },
  })

  return (
    <ResponsivePage.Body variant="fullscreen" showHeader={false}>
      <EditModeLayout
        defaultSection={AvailableSections.basic}
        options={options()}
        isDirty={form.dirty}
        isDisabled={!form.dirty}
        setSaved={() => form.handleSubmit()}
      >
        <FormEditMode
          data={form.values}
          onDataChange={form.setValues}
          loading={loading}
          selected={selectedSection}
        />
      </EditModeLayout>
    </ResponsivePage.Body>
  )
}

export default EditMode
